import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type IconName } from '@/components/Icon'
import { match } from '@/utils/pattern-match-util'

/**
 * How a billing method is shown.
 */
export type BillingMethodVariant = 'credit' | 'card' | 'unknown'

/**
 * What a billing method list item shows.
 */
export interface BillingMethodDisplayData {
  variant: BillingMethodVariant
  title: string
  workspaceIcon: string
  balance: number
  remainingBalance: number
  cardIcon: IconName
  last4: string
  expMonth: number
  expYear: number
  isDefault: boolean
}

/**
 * Flattens a billing method into what its list item shows.
 */
export function mapBillingMethodToDisplayData(
  method: BillingMethod,
): BillingMethodDisplayData {
  const workspace = method.billingProfile.workspace
  const base = {
    workspaceIcon: workspace.branding.logos.icon,
    balance: 0,
    remainingBalance: 0,
    cardIcon: 'info' as const,
    last4: '',
    expMonth: 0,
    expYear: 0,
    isDefault: method.isDefault,
  }
  switch (method.type.type) {
    case 'StripeCardPaymentMethod':
      return {
        ...base,
        variant: 'card',
        title: match<string, string>(method.type.brand, {
          visa: () => 'Visa',
          mastercard: () => 'MasterCard',
          discover: () => 'Discover',
          amex: () => 'American Express',
          [match.DEFAULT]: () => 'Info',
        }),
        cardIcon: match<string, IconName>(method.type.brand, {
          visa: () => 'visa',
          mastercard: () => 'mastercard',
          discover: () => 'discover',
          amex: () => 'amex',
          [match.DEFAULT]: () => 'info',
        }),
        last4: method.type.last4,
        expMonth: method.type.expMonth,
        expYear: method.type.expYear,
      }
    case 'Stripe':
      return {
        ...base,
        variant: 'card',
        title: method.type.brand,
        cardIcon: match<string, IconName>(method.type.brand, {
          Visa: () => 'visa',
          MasterCard: () => 'mastercard',
          Discover: () => 'discover',
          'American Express': () => 'amex',
          [match.DEFAULT]: () => 'info',
        }),
        last4: method.type.last4,
        expMonth: method.type.expMonth,
        expYear: method.type.expYear,
      }
    case 'Credit':
      return {
        ...base,
        variant: 'credit',
        title: `${workspace.name} Credit`,
        balance: method.type.balance,
        remainingBalance: method.type.balance + method.type.limit,
      }
    case 'Unknown':
      return {
        ...base,
        variant: 'unknown',
        title: `Unknown Payment Method "${method.type.rawType}"`,
      }
  }
}
